import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';

function newProjectId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return 'proj-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function fmtDue(d) {
  if (!d) return 'No date';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function CustomProjects() {
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');
  const [selected, setSelected] = useState([]); // clientIds
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Active clients
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived)),
      err => console.error('clients snapshot error:', err)
    );
    return unsub;
  }, []);

  // All open custom-project tasks (grouped below into projects)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'tasks'), where('source', '==', 'custom-project')),
      snap => setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => !t.completed)),
      err => console.error('tasks snapshot error:', err)
    );
    return unsub;
  }, []);

  const allSelected = clients.length > 0 && selected.length === clients.length;

  const toggleClient = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };
  const toggleAll = () => {
    setSelected(allSelected ? [] : clients.map(c => c.id));
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Add a task name.'); return; }
    if (!date) { setError('Pick a date.'); return; }
    if (selected.length === 0) { setError('Select at least one client.'); return; }
    setSaving(true);
    setError('');
    try {
      const projectId = newProjectId();
      const dueDate = new Date(date + 'T12:00:00');
      await Promise.all(selected.map(id => {
        const c = clients.find(x => x.id === id);
        return addDoc(collection(db, 'tasks'), {
          clientId: id,
          clientName: c?.businessName || '',
          title: title.trim(),
          instructions: notes.trim(),
          dueDate,
          taskType: 'Custom',
          source: 'custom-project',
          projectId,
          completed: false,
          createdAt: serverTimestamp(),
        });
      }));
      setTitle('');
      setNotes('');
      setDate('');
      setSelected([]);
    } catch (e) {
      console.error('Failed to save custom project:', e);
      setError('Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (items) => {
    if (!window.confirm('Delete this project for all listed clients?')) return;
    await Promise.all(items.map(t => deleteDoc(doc(db, 'tasks', t.id))));
  };

  const getDue = (t) => t.dueDate?.toDate ? t.dueDate.toDate() : t.dueDate ? new Date(t.dueDate) : null;

  // Group open tasks into projects by projectId
  const groups = {};
  tasks.forEach(t => {
    const key = t.projectId || t.id;
    if (!groups[key]) groups[key] = { key, title: t.title, notes: t.instructions, due: getDue(t), items: [] };
    groups[key].items.push(t);
  });
  const projectList = Object.values(groups)
    .map(g => ({ ...g, items: g.items.sort((a, b) => (a.clientName || '').localeCompare(b.clientName || '')) }))
    .sort((a, b) => (a.due?.getTime() || Infinity) - (b.due?.getTime() || Infinity));

  return (
    <div className="two-col" style={{ padding: 24 }}>
      {/* Create */}
      <div className="card" style={{ height: 'fit-content' }}>
        <div className="card-title">New Custom Project</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
          Schedule a one-off task for one or more clients. It shows up on the Dashboard under “Scheduled Tasks” on its date.
        </div>

        {error && <div className="error-box" style={{ marginBottom: 12, fontSize: 12 }}>{error}</div>}

        <div className="form-field">
          <label>Task Name</label>
          <input type="text" placeholder="e.g. Contact about continuing campaign" value={title} onChange={e => { setTitle(e.target.value); setError(''); }} />
        </div>
        <div className="form-field">
          <label>Date</label>
          <input type="date" value={date} onChange={e => { setDate(e.target.value); setError(''); }} />
        </div>
        <div className="form-field">
          <label>Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <textarea placeholder="Any details Tiana needs…" value={notes} onChange={e => setNotes(e.target.value)} style={{ minHeight: 64 }} />
        </div>

        <div className="form-field">
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Clients</span>
            <button type="button" onClick={toggleAll} style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', fontSize: 12, cursor: 'pointer', padding: 0 }}>
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </label>
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px' }}>
            {clients.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No active clients.</div>
            ) : clients.map(c => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleClient(c.id)} style={{ accentColor: 'var(--accent-blue)' }} />
                {c.businessName}
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{selected.length} selected</div>
          )}
        </div>

        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Schedule Project'}
        </button>
      </div>

      {/* Existing scheduled projects */}
      <div className="card">
        <div className="card-title">Scheduled Projects</div>
        {projectList.length === 0 ? (
          <div className="empty-state">No scheduled projects</div>
        ) : (
          projectList.map(g => (
            <div key={g.key} className="global-project-item" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="global-project-title">{g.title || '(Untitled)'}</div>
                <div className="global-project-meta">{fmtDue(g.due)}</div>
                {g.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{g.notes}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {g.items.map(t => (
                    <span key={t.id} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px' }}>
                      {t.clientName || 'Client'}
                    </span>
                  ))}
                </div>
              </div>
              <button className="btn btn-sm btn-danger" onClick={() => deleteProject(g.items)}>🗑</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
